<script>
import { required } from "@vuelidate/validators";
import { password } from "@/utils/form-validators";
import Button from "@/components/atoms/button";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import StateOverlay from "@/components/molecules/state-overlay";
import Confirmation from "@/components/organisms/reset-mdp-form/confirmation";
import Captcha from "@/components/molecules/captcha";
import { states } from "@/components/molecules/mixins/form.mixin";
import PasswordRequirements from "@/components/organisms/inscription-form/password-requirements";

export default {
  name: "ModifierMotDePasse",
  components: {
    Button,
    FormInput,
    FormGroup,
    Form,
    StateOverlay,
    Confirmation,
    Captcha,
    PasswordRequirements
  },
  props: {
    state: {
      type: String,
      required: false,
      default: ""
    },
    email: {
      type: String,
      required: false,
      default: ""
    }
  },
  emits: ["save"],
  setup(props) {
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);
    return {
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      model: {
        password: "",
        password_confirmation: ""
      },
      captchaToken: null
    };
  },
  validations() {
    return {
      model: {
        password: {
          required,
          password
        },
        password_confirmation: {
          required,
          isValidConfirmation(value) {
            return this.model.password === value;
          }
        }
      }
    };
  },
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        password: this.model.password,
        captcha: this.captchaToken
      };
      this.$emit("save", model);
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    }
  }
};
</script>

<template>
  <div class="u-pt-sm">
    <template v-if="state === 'success'">
      <Confirmation :email="email" />
    </template>
    <template v-else>
      <div class="c-reset-mdp-form">
        <h1 class="u-pt-md u-font-24 u-color-primary u-mb-md u-text-center">
          Réinitialisation du mot de passe
        </h1>
        <p class="u-text-center u-mt-md u-mb-md">
          Merci de saisir votre nouveau mot de passe.
        </p>

        <Form
          :error="error"
          @submit.prevent="save"
        >
          <StateOverlay
            :is-loading="state === states.LOADING"
            overlay
          >
            <FormGroup
              block
              class="u-flex u-mb-md"
              size="xlg"
              :state="getState(v$,'password', 'model')"
            >
              <FormInput
                v-model="model.password"
                size="xlg"
                type="password"
                :state="getState(v$,'password', 'model')"
                @blur="v$.model.password.$touch()"
                @input="v$.model.password.$reset()"
              >
                Mot de passe *
              </FormInput>
              <template #message>
                <p>
                  <template v-if="v$.model.password.required.$invalid">
                    Merci de renseigner votre nouveau mot de passe
                  </template>
                  <template v-else>
                    {{ getErrorMessage("password","Merci de renseigner un mot de passe valide") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <FormGroup
              class="u-flex u-mb-md"
              size="xlg"
              :state="getState(v$,'password_confirmation', 'model')"
            >
              <FormInput
                v-model="model.password_confirmation"
                size="xlg"
                type="password"
                :state="getState(v$,'password_confirmation', 'model')"
                @blur="v$.model.password_confirmation.$touch()"
                @input="v$.model.password_confirmation.$reset()"
              >
                Confirmer mot de passe *
              </FormInput>
              <template #message>
                <p>
                  <template v-if="v$.model.password_confirmation.required.$invalid">
                    Merci de renseigner votre nouveau mot de passe
                  </template>
                  <template v-else>
                    {{ getErrorMessage("password_confirmation","Merci de renseigner un mot de passe valide et identique") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <div class="c-reset-mdp-form__password-requirements">
              <p class="u-mr-lg u-mb-xs">
                Pour garantir la sécurité de votre compte, votre mot de passe doit contenir au minimum :
              </p>
              <PasswordRequirements :password="model.password" />
            </div>
            <p class="c-reset-mdp-form__field-required">
              * champs obligatoires
            </p>
            <Captcha
              ref="captcha"
              @verify="verifyCaptcha"
            />
            <div class="u-text-center u-mt-lg">
              <Button
                variant="secondary"
                size="md"
                radius="full"
                type="submit"
                value="Réinitialiser"
              />
            </div>
          </StateOverlay>
        </Form>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/reset-mdp-form.scss";
</style>
